import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, AttendeeStack } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { eventService } from '../services'
import { useEventDialog } from '../hooks'
import { FieldDefManager } from '../components'
import GuestList from './GuestList'
import { appName } from '../appConfig'

const TABS = [
  { key: 'guests', label: 'Gästlista' },
  { key: 'fields', label: 'Anpassade fält' },
]

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : ''

const formatTime = (value) => value
  ? new Date(value).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
  : ''

const isSameDay = (a, b) => {
  const d1 = new Date(a), d2 = new Date(b)
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

// A same-day event reads "12 sep. 2026 10:00 – 12:00", not the date twice.
const formatRange = (start, end) => {
  if (!start) return ''
  if (!end) return formatDateTime(start)
  return isSameDay(start, end)
    ? `${formatDateTime(start)} – ${formatTime(end)}`
    : `${formatDateTime(start)} – ${formatDateTime(end)}`
}

// The event's own page: its metadata, the internal attendee stack (event_interest) and the
// external guest list (event_guest) side by side, since one event can have both.
// Keyed by slug, like every other event route — the uuid is not a URL handle here.
const EventDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteEventDialog } = useEventDialog()

  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('guests')
  const [preview, setPreview] = useState(null) // { subject, html } | null
  const [previewLoading, setPreviewLoading] = useState(false)

  const fetchEvent = () => {
    setLoading(true)
    eventService.getBySlug(slug)
      .then(res => setEvent(res.event))
      .catch(() => showMessage('Kunde inte ladda event', 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchEvent() }, [slug]) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteEvent = () => {
    // HEAD's dialog shape: { title, slug } — not the branch's event_name/event_uuid.
    showDeleteEventDialog({ title: event.event_title, slug: event.event_slug }, async () => {
      await eventService.remove(event.event_slug)
        .then(() => { hideModalDialog(); showMessage('Borttaget'); navigate(`/${appName}/list`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  const openPreview = () => {
    setPreviewLoading(true)
    eventService.getInvitePreview(event.event_slug)
      .then(res => setPreview({ subject: res.subject, html: res.html }))
      .catch(err => showMessage(err.message || 'Kunde inte förhandsgranska inbjudan', 'error'))
      .finally(() => setPreviewLoading(false))
  }

  const toggleInterest = () => {
    eventService.toggleInterest(event.event_slug)
      .then(res => setEvent(prev => ({
        ...prev,
        interest_count: res.interest_count,
        user_has_marked_interest: res.user_has_marked_interest,
        interested_users: res.interested_users,
      })))
      .catch(err => showMessage(err.message || 'Kunde inte uppdatera', 'error'))
  }

  if (loading || !event) return <div className="ev-page"><p className="ev-empty">Laddar…</p></div>

  return (
    <div className="ev-page ev-page-detail">
      <div className="ev-page-header">
        <h1>{event.event_title}</h1>
        {/* HEAD's existing class — .ev-page-header-actions already carries the flex row +
            gap + nowrap-button rules. A new name here left the buttons stacking. */}
        <div className="ev-page-header-actions">
          <Button value={previewLoading ? 'Laddar…' : 'Förhandsgranska inbjudan'} className="button" onClick={openPreview} />
          <Button value="Redigera" className="button" onClick={() => navigate(`/${appName}/${event.event_slug}/edit`)} />
          <Button value="Ta bort" className="button ev-danger-button" onClick={deleteEvent} />
        </div>
      </div>

      <div className="ev-meta-row">
        {event.event_location &&
          <div className="ev-meta-item"><div className="ev-meta-label">Plats</div><div className="ev-meta-value">{event.event_location}</div></div>
        }
        {event.event_start_timestamp &&
          <div className="ev-meta-item">
            <div className="ev-meta-label">Datum</div>
            <div className="ev-meta-value">{formatRange(event.event_start_timestamp, event.event_end_timestamp)}</div>
          </div>
        }
        {/* Organizer is nullable — a calendar-only event has none, so don't render an
            empty row for it. */}
        {event.organizer_fullname &&
          <div className="ev-meta-item"><div className="ev-meta-label">Arrangör</div><div className="ev-meta-value">{event.organizer_fullname}</div></div>
        }
        <div className="ev-meta-item">
          <div className="ev-meta-label">Interna deltagare</div>
          <div className="ev-meta-value">
            <AttendeeStack
              users={event.interested_users || []}
              isParticipant={!!event.user_has_marked_interest}
              onToggle={toggleInterest}
              joinTitle="Markera intresse"
              leaveTitle="Ta bort intresse"
            />
          </div>
        </div>
      </div>

      <div className="ev-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ev-tab-button${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      <div className="ev-section">
        {activeTab === 'guests' && <GuestList event={event} />}
        {activeTab === 'fields' && <FieldDefManager eventSlug={event.event_slug} />}
      </div>

      {preview &&
        <div className="ev-preview-overlay" onClick={() => setPreview(null)}>
          <div className="ev-preview-panel" onClick={e => e.stopPropagation()}>
            <div className="ev-preview-bar">
              <span>Förhandsgranska inbjudan</span>
              <Button value="Stäng ✕" className="button" onClick={() => setPreview(null)} />
            </div>
            <div className="ev-preview-subject">Ämne: {preview.subject}</div>
            <div className="ev-preview-caption">Exempel — varje gäst får sina egna uppgifter ifyllda.</div>
            <iframe className="ev-preview-frame" srcDoc={preview.html} title="Förhandsgranska inbjudan" />
          </div>
        </div>
      }
    </div>
  )
}

export default EventDetail
